import React, { useEffect, useState } from 'react';
import { getRetailerVisualizerLink } from '../../services/api.js';
import colors from '../../theme/colors';

/**
 * Shows the retailer's shareable visualizer link with a copy button.
 * Fetches the link on mount using the retailer's auth token.
 */
export default function VisualizerLinkCard() {
  const [link, setLink] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    const fetchLink = async () => {
      try {
        const token = localStorage.getItem('retailer_token');
        
        const res = await getRetailerVisualizerLink();
        // backend shape: { success: true, data: { visualizer_link, visualizer_token, ... } }
        setLink(res?.data?.visualizer_link || '');
      } catch (err) {
        console.error(err);
        setError('Failed to load visualizer link');
      } finally {
        setLoading(false);
      }
    };
    fetchLink();
  }, []);

  const handleCopy = async () => {
    if (!link) return;
    try {
      await navigator.clipboard.writeText(link);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error('Copy failed', err);
    }
  };

  return (
    <div 
      className="rounded-xl border p-8 shadow-sm"
      style={{ backgroundColor: colors.surface, borderColor: colors.border }}
    >
      <div className="flex items-center justify-between mb-4">
        <h3 
          className="text-lg font-semibold"
          style={{ color: colors.t1 }}
        >
          Your Visualizer Link
        </h3>
        {!loading && link && (
          <span 
            className="text-xs font-medium px-2.5 py-1 rounded-full"
            style={{ 
              backgroundColor: colors.success + '20', 
              color: colors.success 
            }}
          >
            Live
          </span>
        )}
      </div>
      
      <p 
        className="text-sm mb-4"
        style={{ color: colors.t2 }}
      >
        Share this link with customers or embed it on your website. Anyone with this link can try your products in the visualizer.
      </p>

      {loading && (
        <div 
          className="h-11 rounded-lg animate-pulse"
          style={{ backgroundColor: colors.border + '40' }}
        />
      )}

      {!loading && error && (
        <p 
          className="text-sm rounded-lg px-4 py-3"
          style={{ 
            backgroundColor: '#fee2e2', 
            color: '#dc2626',
            border: '1px solid #fecaca'
          }}
        >
          {error}
        </p>
      )}

      {!loading && !error && link && (
        <div className="flex flex-col sm:flex-row gap-3">
          <div 
            className="flex-1 flex items-center gap-2 px-4 py-2.5 rounded-lg border"
            style={{ 
              backgroundColor: colors.background,
              borderColor: colors.border
            }}
          >
            <svg 
              className="w-4 h-4 flex-shrink-0" 
              fill="none" 
              stroke="currentColor" 
              viewBox="0 0 24 24"
              style={{ color: colors.t2 }}
            >
              <path 
                strokeLinecap="round" 
                strokeLinejoin="round" 
                strokeWidth={2} 
                d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1" 
              />
            </svg>
            <input
              type="text"
              readOnly
              value={link}
              onClick={(e) => e.target.select()}
              className="flex-1 text-sm bg-transparent outline-none truncate"
              style={{ color: colors.t1 }}
            />
          </div>
          <div className="flex gap-2">
            <button
              onClick={handleCopy}
              className={`text-sm font-semibold px-5 py-2.5 rounded-lg transition-all whitespace-nowrap ${
                copied
                  ? 'text-white'
                  : 'text-white'
              }`}
              style={{ 
                backgroundColor: copied ? colors.success : colors.accent
              }}
              onMouseEnter={(e) => !copied && (e.target.style.backgroundColor = colors.accent2)}
              onMouseLeave={(e) => !copied && (e.target.style.backgroundColor = colors.accent)}
            >
              {copied ? 'Copied ✓' : 'Copy Link'}
            </button>
            <a
              href={link}
              target="_blank"
              rel="noopener noreferrer"
              className="text-sm font-semibold px-5 py-2.5 rounded-lg transition-all whitespace-nowrap"
              style={{ 
                backgroundColor: colors.border + '40',
                color: colors.t1
              }}
              onMouseEnter={(e) => e.target.style.backgroundColor = colors.border + '60'}
              onMouseLeave={(e) => e.target.style.backgroundColor = colors.border + '40'}
            >
              Open ↗
            </a>
          </div>
        </div>
      )}
    </div>
  );
}